<!-- Complaints/Tickets Management Page -->
<div class="p-4 md:p-8 min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50 to-teal-50">
  
  <!-- Page Header -->
  <div class="mb-8">
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-3xl md:text-4xl font-bold text-slate-800 mb-2">Complaints Management</h1>
        <p class="text-slate-600">Review and resolve customer complaints and support tickets</p>
      </div>
      
      <button 
        (click)="load()"
        class="p-3 bg-white hover:bg-slate-50 text-slate-600 rounded-xl shadow-md border border-slate-200 transition-all hover:scale-105 active:scale-95"
        [class.animate-spin]="loading">
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
        </svg>
      </button>
    </div>
  </div>

  <!-- Complaints List -->
  <div class="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
    <div class="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-5">
      <div class="flex items-center gap-3">
        <div class="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
          <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"></path>
          </svg>
        </div>
        <div>
          <h2 class="text-xl font-bold text-white">All Tickets</h2>
          <p class="text-sm text-emerald-100">{{ complaints.length }} tickets total</p>
        </div>
      </div>
    </div>

    <div class="overflow-x-auto">
      <table class="w-full">
        <thead>
          <tr class="bg-slate-50 border-b-2 border-slate-200 text-slate-700">
            <th class="px-6 py-4 text-left font-bold uppercase tracking-wider text-sm">Ticket Info</th>
            <th class="px-6 py-4 text-left font-bold uppercase tracking-wider text-sm">Customer</th>
            <th class="px-6 py-4 text-left font-bold uppercase tracking-wider text-sm">Category</th>
            <th class="px-6 py-4 text-left font-bold uppercase tracking-wider text-sm">Priority</th>
            <th class="px-6 py-4 text-left font-bold uppercase tracking-wider text-sm">Status</th>
            <th class="px-6 py-4 text-center font-bold uppercase tracking-wider text-sm">Actions</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-200">
          <tr *ngFor="let c of complaints" class="hover:bg-emerald-50/50 transition-colors group">
            <td class="px-6 py-4">
              <div class="font-bold text-slate-800">{{ c.ticket_number }}</div>
              <div class="text-xs text-slate-500">{{ c.created_at | date:'medium' }}</div>
            </td>
            <td class="px-6 py-4">
              <div class="font-medium text-slate-700">{{ c.customer_name || 'Anonymous' }}</div>
              <div class="text-xs text-slate-500">{{ c.customer_email || 'No email' }}</div>
            </td>
            <td class="px-6 py-4 text-slate-600">{{ c.category || 'General' }}</td>
            <td class="px-6 py-4">
              <span class="px-3 py-1 rounded-full text-xs font-bold border" [ngClass]="getPriorityColor(c.priority)">
                {{ c.priority | uppercase }}
              </span>
            </td>
            <td class="px-6 py-4">
              <span class="px-2 py-1 rounded-lg text-xs font-semibold" [ngClass]="getStatusColor(c.status)">
                {{ c.status | uppercase }}
              </span>
            </td>
            <td class="px-6 py-4">
              <div class="flex items-center justify-center gap-2">
                <button 
                  (click)="selectComplaint(c)"
                  class="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-all">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                  </svg>
                </button>
                <div class="flex gap-1" *ngIf="c.status !== 'resolved'">
                  <button 
                    [disabled]="updatingStatus === c.id"
                    (click)="updateStatus(c.id, 'resolved')"
                    class="px-3 py-1 bg-emerald-500 text-white rounded-lg text-xs font-bold hover:bg-emerald-600 transition-all shadow-sm">
                    Resolve
                  </button>
                </div>
              </div>
            </td>
          </tr>
          <!-- Empty State -->
          <tr *ngIf="complaints.length === 0 && !loading">
            <td colspan="6" class="px-6 py-12 text-center">
              <div class="text-slate-400 mb-2">
                <svg class="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"></path>
                </svg>
              </div>
              <p class="text-lg font-medium text-slate-600">No complaints found</p>
              <p class="text-sm text-slate-500">New tickets will appear here as they are created</p>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- Detail Modal/Slideover (Conditional) -->
  <div *ngIf="selectedComplaint" class="fixed inset-0 z-50 flex items-center justify-end bg-slate-900/40 backdrop-blur-sm" style="animation: fadeIn 0.2s ease-out;">
    <div class="w-full max-w-lg h-full bg-white shadow-2xl p-8" style="animation: slideLeft 0.3s ease-out;">
      <div class="flex items-center justify-between mb-8">
        <h2 class="text-2xl font-bold text-slate-800">Ticket Details</h2>
        <button (click)="selectedComplaint = null" class="p-2 hover:bg-slate-100 rounded-lg text-slate-500">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l18 18"></path>
          </svg>
        </button>
      </div>

      <div class="space-y-6 overflow-y-auto max-h-[calc(100vh-150px)] pr-2">
        <div>
          <label class="text-xs font-bold text-slate-400 uppercase tracking-widest">Issue Text</label>
          <div class="mt-2 p-4 bg-slate-50 rounded-xl text-slate-700 leading-relaxed border border-slate-100">
            {{ selectedComplaint.complaint_text }}
          </div>
        </div>

        <div class="grid grid-cols-2 gap-6">
          <div>
            <label class="text-xs font-bold text-slate-400 uppercase tracking-widest">Ticket ID</label>
            <p class="mt-1 font-bold text-slate-800">{{ selectedComplaint.ticket_number }}</p>
          </div>
          <div>
            <label class="text-xs font-bold text-slate-400 uppercase tracking-widest">Date Created</label>
            <p class="mt-1 text-slate-600">{{ selectedComplaint.created_at | date:'medium' }}</p>
          </div>
        </div>

        <div>
          <label class="text-xs font-bold text-slate-400 uppercase tracking-widest">Customer Details</label>
          <div class="mt-3 space-y-2">
            <p class="flex items-center gap-3 text-slate-700">
              <svg class="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
              {{ selectedComplaint.customer_name || 'Anonymous' }}
            </p>
            <p class="flex items-center gap-3 text-slate-700">
              <svg class="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
              {{ selectedComplaint.customer_email || 'N/A' }}
            </p>
            <p class="flex items-center gap-3 text-slate-700">
              <svg class="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5.983C3 4.888 3.895 4 5 4h4c.983 0 1.817.718 1.975 1.69l.542 3.328a2 2 0 01-.948 2.046c-.714.42-1.282.903-1.688 1.458a11.042 11.042 0 005.516 5.516c.555-.406 1.038-.974 1.458-1.688a2 2 0 012.046-.948l3.328.542c.972.158 1.69.992 1.69 1.975v4c0 1.105-.895 2-2 2h-1c-5.985 0-11-5.015-11-11v-1z"></path></svg>
              {{ selectedComplaint.customer_phone || 'N/A' }}
            </p>
          </div>
        </div>

        <div class="pt-6 border-t border-slate-100">
          <button 
            *ngIf="selectedComplaint.status !== 'resolved'"
            (click)="updateStatus(selectedComplaint.id, 'resolved'); selectedComplaint = null"
            class="w-full py-4 bg-emerald-500 text-white font-bold rounded-2xl hover:bg-emerald-600 transition-all shadow-lg hover:shadow-emerald-200">
            Mark as Resolved
          </button>
        </div>
      </div>
    </div>
  </div>
</div>
